import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Package, CalendarPlus, MessageCircle, CheckCircle2, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../lib/api';

const MONTH_NAMES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const PickupScheduler = ({ companyInfo }) => {
  const [citaLoading, setCitaLoading] = useState(false);
  const [citaSuccess, setCitaSuccess] = useState(false);
  const [citaForm, setCitaForm] = useState({
    nombre_cliente: '', telefono_cliente: '', direccion: '',
    fecha_programada: '', hora_inicio: '09:00', articulos_lista: ''
  });
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [selectedDate, setSelectedDate] = useState('');

  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
  const firstDay = new Date(calYear, calMonth, 1).getDay();

  const prevMonth = () => { if (calMonth === 0) { setCalMonth(11); setCalYear(calYear - 1); } else setCalMonth(calMonth - 1); };
  const nextMonth = () => { if (calMonth === 11) { setCalMonth(0); setCalYear(calYear + 1); } else setCalMonth(calMonth + 1); };

  const selectCalDate = (day) => {
    const d = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSelectedDate(d);
    setCitaForm(prev => ({ ...prev, fecha_programada: d }));
  };

  const handleCita = async (e) => {
    e.preventDefault();
    setCitaLoading(true);
    try {
      await api.post('/pickups/public/agendar', citaForm);
      setCitaSuccess(true);
      toast.success('Cita agendada!');
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Completa todos los campos');
    }
    setCitaLoading(false);
  };

  const resetForm = () => {
    setCitaSuccess(false);
    setCitaForm({ nombre_cliente: '', telefono_cliente: '', direccion: '', fecha_programada: '', hora_inicio: '09:00', articulos_lista: '' });
    setSelectedDate('');
  };

  if (citaSuccess) {
    return (
      <div className="bg-white/5 rounded-2xl border border-white/10 p-4 sm:p-6">
        <div className="text-center py-8 space-y-4">
          <CheckCircle2 className="h-20 w-20 text-green-400 mx-auto" />
          <h3 className="text-2xl font-black text-green-400">Cita Agendada!</h3>
          <p className="text-white/60">Te contactaremos para confirmar la recogida</p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-sm mx-auto">
            <Button variant="outline" className="flex-1 border-white/20 text-white hover:bg-white/10 bg-transparent" onClick={resetForm}>
              Nueva Cita
            </Button>
            <a href={`https://wa.me/${companyInfo.telefono_whatsapp}?text=${encodeURIComponent('Hola! Acabo de agendar una recogida.')}`} target="_blank" rel="noreferrer" className="flex-1">
              <Button className="w-full bg-green-500 hover:bg-green-600"><MessageCircle className="mr-2 h-4 w-4" /> Confirmar WhatsApp</Button>
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white/5 rounded-2xl border border-white/10 p-4 sm:p-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {/* Calendar */}
        <div>
          <h3 className="text-base font-bold mb-3 flex items-center gap-2"><CalendarPlus className="h-5 w-5 text-orange-400" /> Selecciona una Fecha</h3>
          <div className="bg-white/5 rounded-xl border border-white/10 p-3">
            <div className="flex items-center justify-between mb-3">
              <button type="button" onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-white/10"><ChevronLeft className="h-5 w-5" /></button>
              <span className="font-bold text-base">{MONTH_NAMES[calMonth]} {calYear}</span>
              <button type="button" onClick={nextMonth} className="p-1.5 rounded-lg hover:bg-white/10"><ChevronRight className="h-5 w-5" /></button>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center text-xs mb-1">
              {['Do','Lu','Ma','Mi','Ju','Vi','Sa'].map(d => <span key={d} className="text-white/40 font-bold py-1">{d}</span>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {Array(firstDay).fill(null).map((_, i) => <div key={`e-${i}`} />)}
              {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
                const dateStr = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const isPast = dateStr < todayStr;
                const isToday = dateStr === todayStr;
                const isSelected = dateStr === selectedDate;
                return (
                  <button key={day} type="button" disabled={isPast}
                    onClick={() => selectCalDate(day)}
                    className={`aspect-square rounded-lg text-sm font-bold transition-all
                      ${isPast ? 'text-white/15 cursor-not-allowed' : 'hover:bg-blue-600/50 cursor-pointer'}
                      ${isToday ? 'ring-2 ring-blue-400 text-blue-400' : ''}
                      ${isSelected ? 'bg-orange-500 text-white shadow-lg' : ''}`}>
                    {day}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleCita} className="space-y-3">
          <h3 className="text-base font-bold flex items-center gap-2"><Package className="h-5 w-5 text-blue-400" /> Datos de Recogida</h3>
          <div>
            <Label className="text-xs font-bold text-white/60">Nombre *</Label>
            <Input data-testid="cita-nombre" value={citaForm.nombre_cliente} onChange={(e) => setCitaForm({...citaForm, nombre_cliente: e.target.value})}
              placeholder="Tu nombre completo" className="h-11 bg-white/10 border-white/20 text-white placeholder:text-white/30 mt-1" required />
          </div>
          <div>
            <Label className="text-xs font-bold text-white/60">Telefono *</Label>
            <Input data-testid="cita-telefono" value={citaForm.telefono_cliente} onChange={(e) => setCitaForm({...citaForm, telefono_cliente: e.target.value})}
              placeholder="(201) 000-0000" className="h-11 bg-white/10 border-white/20 text-white placeholder:text-white/30 mt-1" required />
          </div>
          <div>
            <Label className="text-xs font-bold text-white/60">Direccion de Recogida *</Label>
            <Input data-testid="cita-direccion" value={citaForm.direccion} onChange={(e) => setCitaForm({...citaForm, direccion: e.target.value})}
              placeholder="Direccion completa" className="h-11 bg-white/10 border-white/20 text-white placeholder:text-white/30 mt-1" required />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs font-bold text-white/60">Fecha *</Label>
              <Input data-testid="cita-fecha" type="date" value={citaForm.fecha_programada}
                onChange={(e) => { setCitaForm({...citaForm, fecha_programada: e.target.value}); setSelectedDate(e.target.value); }}
                min={todayStr} className="h-11 bg-white/10 border-white/20 text-white mt-1" required />
            </div>
            <div>
              <Label className="text-xs font-bold text-white/60">Hora</Label>
              <Input data-testid="cita-hora" type="time" value={citaForm.hora_inicio}
                onChange={(e) => setCitaForm({...citaForm, hora_inicio: e.target.value})} className="h-11 bg-white/10 border-white/20 text-white mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold text-white/60">Que envias?</Label>
            <Input data-testid="cita-articulos" value={citaForm.articulos_lista} onChange={(e) => setCitaForm({...citaForm, articulos_lista: e.target.value})}
              placeholder="Ej: 3 cajas, 1 nevera..." className="h-11 bg-white/10 border-white/20 text-white placeholder:text-white/30 mt-1" />
          </div>
          <Button data-testid="cita-submit" type="submit" disabled={citaLoading}
            className="w-full h-12 text-base font-bold bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 shadow-xl shadow-orange-500/20 rounded-xl">
            {citaLoading ? 'Agendando...' : (<><CalendarPlus className="mr-2 h-5 w-5" /> Agendar Recogida Gratis</>)}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default PickupScheduler;
